import { makeStyles } from '@material-ui/core/styles';
import { InfiniteScrollGrid } from '@roc/feature-app-core';
import {
  useBorrowerLoansRoutes,
  useBorrowerLoansStore,
} from '@roc/feature-borrower-loans';
import {
  DataGrid,
  GridColumnProps,
  LinkColumn,
  StatusChip,
  StatusType,
} from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import { CapitalProvider } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

const usdAmount: any = params => {
  if (params.value === '-') {
    return params.value;
  }
  return params.value && formatCurrency(Number(params.value));
};

export const borrowerLoansColumns: GridColumnProps[] = [
  {
    field: 'dealid',
    headerName: 'Loan Id',
    maxWidth: 120,
    cellRenderer: 'loanIdCellRenderer',
  },
  {
    field: 'dealName',
    headerName: 'Deal Name',
    minWidth: 200,
    cellRenderer: 'dealNameCellRenderer',
  },
  {
    field: 'status',
    headerName: 'Status',
    maxWidth: 170,
    cellRenderer: 'statusCellRenderer',
  },
  {
    field: 'loanSubType',
    headerName: 'Loan Type',
    cellRenderer: 'loanSubTypeCellRenderer',
    maxWidth: 300,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    maxWidth: 140,
    valueFormatter: usdAmount,
  },
  {
    field: 'submissionDate',
    headerName: 'Submission Date',
    maxWidth: 185,
  },
  {
    field: 'closingDate',
    headerName: 'Closing Date',
    maxWidth: 185,
  },
];

const useStyles = makeStyles(theme => ({
  paginationBar: {
    border: 'none',
    borderTop: '1px solid #eee',
    padding: 8,
    background: '#fafafa',
  },
}));

export const borrowerLoansFrameworkComponents = (
  borrowerLoanRoutesConfig,
  platformBorrowerLoanRoutesConfig
) => {
  const loanSubTypeCellRenderer = params => {
    const loanSubType = params.node?.data?.loanSubType;
    return (
      <>
        <span>{loanSubType}</span>
      </>
    );
  };

  const loanDetailsLinkCellRenderer = params => {
    const loanId = params.node?.data?.dealid;
    // Platform-sourced ("EXTERNAL_CAPITAL") loans are mounted under `p-loans/...` instead of
    // `loans/...` (see borrowerLoanRoutes.tsx) — the embedded platform-ui web component
    // (BorrowerLoanOverviewPlatformUi) detects this from the path to know to fetch data from the
    // platform rather than roc.
    const isExternalCapital =
      params.node?.data?.capitalProvider === CapitalProvider.EXTERNAL_CAPITAL;
    const routesConfig = isExternalCapital
      ? platformBorrowerLoanRoutesConfig
      : borrowerLoanRoutesConfig;
    const url = routesConfig.loans(loanId).children.dashboard.url;
    return LinkColumn({ ...params, url });
  };

  return {
    loanIdCellRenderer: loanDetailsLinkCellRenderer,
    dealNameCellRenderer: loanDetailsLinkCellRenderer,
    loanSubTypeCellRenderer: loanSubTypeCellRenderer,
    statusCellRenderer: params =>
      params.value ? (
        <StatusChip statusType={StatusType.LOAN} label={params.value} size="small" displayName={params.data?.statusName || undefined} />
      ) : null,
  };
};

export const BorrowerLoansMyLoansGrid = observer(() => {
  const classes = useStyles();
  const { borrowerLoansStore } = useBorrowerLoansStore();
  const { borrowerLoansGridStore } = borrowerLoansStore;
  const gridStore = borrowerLoansGridStore;
  const {
    borrowerLoanRoutesConfig,
    platformBorrowerLoanRoutesConfig,
  } = useBorrowerLoansRoutes();

  useEffect(() => {
    gridStore.resetAndFetchGridData();
  }, []);

  return (
    <InfiniteScrollGrid
      columns={borrowerLoansColumns}
      store={gridStore}
      frameworkComponents={borrowerLoansFrameworkComponents(
        borrowerLoanRoutesConfig,
        platformBorrowerLoanRoutesConfig
      )}
      paginationBarClassName={classes.paginationBar}
      domLayout="autoHeight"
    />
  );
});
